<template>
  <div ref="dropdown" class="dropdown-container position-relative">
    <input :value="searchTerm" type="text" ref="inputRef"
      class="dropdown-input dropdown border-default bg-primary-white w-100 outline-none d-flex align-items-center dropdown-input-text"
      :class="{ focused: props.dropdownOpen, 'bg-grey-200': disabled }" @focus="() => emit('open')" @input="handleInput"
      :placeholder="placeholder" :disabled="disabled" />
    <div class="label" :class="{ 'text-primary-blue': props.dropdownOpen, 'bg-grey-200': disabled }" style="z-index: 1">
      {{ label }}
    </div>
    <div class="dropdown-menu show w-100 border-0 px-3 px-md-0" :class="[props.dropdownOpen ? '' : 'd-none']">
      <slot name="dropdown"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, defineExpose } from "vue"
import { onClickOutside } from "@vueuse/core"
import screenType from "@/vue/planning/computed/screenType"

interface Props {
  label: string
  value: string
  dropdownOpen: boolean
  placeholder?: string
  disabled?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: "close"): void
  (e: "open"): void
  (e: "search", query: string): void
}>()

const dropdown = ref<HTMLDivElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)
const searchTerm = ref(props.value)

// Sync input when value prop changes externally
watch(() => props.value, (newVal) => {
  searchTerm.value = newVal
})

// Debounced input handler
let debounceTimeout: ReturnType<typeof setTimeout> | null = null
function handleInput(event: Event) {
  const val = (event.target as HTMLInputElement).value
  searchTerm.value = val

  if (debounceTimeout) clearTimeout(debounceTimeout)

  if (val.length === 0) {
    emit("search", "")
    return
  }

  debounceTimeout = setTimeout(() => {
      emit("search", val)
  }, 300)
}

// Handle dropdown open state on mobile
watch(
  () => props.dropdownOpen,
  (newValue) => {
    if (screenType.value !== "mobile") return
    if (newValue) {
      document.body.classList.add("overflow-hidden")
    } else {
      document.body.classList.remove("overflow-hidden")
    }
  }
)

onMounted(() => {
  onClickOutside(dropdown, () => {
    if (props.dropdownOpen) {
      emit("close")
    }
  })
})

defineExpose({
  inputRef
})
</script>

<style scoped>
.dropdown-container {
  min-width: 100px;
  cursor: pointer;
}

.dropdown-input {
  padding: 8px;
  border-radius: var(--border-radius-default);
}

.dropdown-input.focused {
  box-shadow: inset 0 0 0 1px var(--primary-blue) !important;
}

.dropdown-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 100001 !important;
  max-height: 100%;
  overflow-x: hidden;
  transition: box-shadow 150ms ease-in-out;
}

.dropdown-input-text {
  font-size: 16px;
}

@media (max-width: 1024px) {
  .dropdown-menu {
    background: var(--primary-white);
    height: 100dvh;
  }
}

@media (min-width: 1024px) {
  .dropdown-menu {
    border-radius: 20px;
    position: absolute;
    top: initial;
    bottom: -5px;
    transform: translateY(100%);
    overflow: visible !important;
    max-height: none;
    width: 100%;
    z-index: 901 !important;
  }
}
</style>
